/**
 * Geofence configuration tab.
 * Click center + drag radius, trigger/monitor settings.
 */

import { useState } from 'react';
import { ShieldAlert, Send, X, Loader2 } from 'lucide-react';
import { GEOFENCE_TRIGGERS, GEOFENCE_MONITORS } from '@vtak/shared';
import { useDrawingStore } from '@/stores/drawing-store';
import { ShapeConfig } from './shape-config';
import { sendShapeToTak } from './send-shape';

/** Geofence configuration tab with trigger and monitor settings */
export function GeofenceTab(): React.JSX.Element {
  const { activeTool, drawingState, vertices, radiusMeters, startTool, cancelDrawing, setSending, setSent, reset, shapeConfig, updateConfig } = useDrawingStore();
  const [sendError, setSendError] = useState('');

  const isGeoActive = activeTool === 'geofence';
  const isDrawing = isGeoActive && drawingState === 'drawing';
  const isConfirming = isGeoActive && drawingState === 'confirming';
  const isSending = isGeoActive && drawingState === 'sending';
  const isSent = isGeoActive && drawingState === 'sent';

  async function handleSend(): Promise<void> {
    setSending();
    setSendError('');
    const result = await sendShapeToTak('geofence', vertices, radiusMeters, shapeConfig);
    setSent(result.ok);
    if (!result.ok) setSendError(result.error);
    if (result.ok) setTimeout(reset, 1500);
  }

  return (
    <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
      {!isGeoActive && (
        <button
          onClick={() => startTool('geofence')}
          className="flex items-center justify-center gap-2 rounded bg-echo-yellow px-3 py-2.5 text-sm font-medium text-black"
        >
          <ShieldAlert size={16} /> Place Geofence
        </button>
      )}

      {isDrawing && (
        <div className="space-y-2 rounded bg-white/[0.04] p-3">
          <p className="text-xs text-muted-foreground">
            {vertices.length === 0
              ? 'Click map to place center.'
              : `Center placed. ${radiusMeters > 0 ? `Radius: ${radiusMeters.toFixed(0)}m` : 'Click to set radius.'}`
            }
          </p>
          <button onClick={cancelDrawing} className="flex items-center gap-1 rounded bg-white/[0.06] px-3 py-1.5 text-xs text-red-400">
            <X size={12} /> Cancel
          </button>
        </div>
      )}

      {isConfirming && (
        <div className="space-y-3">
          <div className="text-xs text-muted-foreground">
            Radius: {radiusMeters.toFixed(0)} m
          </div>

          <ShapeConfig />

          <div>
            <label className="text-xs text-muted-foreground">Trigger</label>
            <select
              value={shapeConfig.trigger}
              onChange={(e) => updateConfig({ trigger: e.target.value as 'Entry' | 'Exit' | 'Both' })}
              className="mt-1 w-full rounded bg-white/[0.06] px-3 py-1.5 text-sm text-foreground outline-none"
            >
              {GEOFENCE_TRIGGERS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          <div>
            <label className="text-xs text-muted-foreground">Monitor</label>
            <select
              value={shapeConfig.monitor}
              onChange={(e) => updateConfig({ monitor: e.target.value })}
              className="mt-1 w-full rounded bg-white/[0.06] px-3 py-1.5 text-sm text-foreground outline-none"
            >
              {GEOFENCE_MONITORS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>

          <label className="flex items-center gap-2 text-xs text-foreground">
            <input
              type="checkbox"
              checked={shapeConfig.tracking}
              onChange={(e) => updateConfig({ tracking: e.target.checked })}
              className="accent-echo-yellow"
            />
            Enable Tracking
          </label>

          <label className="flex items-center gap-2 text-xs text-foreground">
            <input
              type="checkbox"
              checked={shapeConfig.elevationMonitored}
              onChange={(e) => updateConfig({ elevationMonitored: e.target.checked })}
              className="accent-echo-yellow"
            />
            Monitor Elevation
          </label>

          <div className="flex gap-2">
            <button onClick={handleSend} className="flex flex-1 items-center justify-center gap-1 rounded bg-echo-yellow px-3 py-2 text-xs font-medium text-black">
              <Send size={12} /> Send to TAK
            </button>
            <button onClick={cancelDrawing} className="rounded bg-white/[0.06] px-3 py-2 text-xs text-red-400">Cancel</button>
          </div>
          {sendError && <p className="text-xs text-red-400">{sendError}</p>}
        </div>
      )}

      {isSending && <p className="text-xs text-muted-foreground"><Loader2 size={14} className="inline animate-spin" /> Sending...</p>}
      {isSent && <p className="text-xs text-green-400">Geofence sent successfully.</p>}
    </div>
  );
}
